import { Link, createFileRoute } from "@tanstack/react-router";
import TitleBar from "@/components/layout/TitleBar";
import PageContent from "@/components/PageContent";
import { getDownloadUrl } from "@/lib/media";

export const Route = createFileRoute('/resources')({
  component: Resources,
})

function Resources() {
  return (
    <>
      <TitleBar />
      <PageContent>
        <section className="mt-4">
          <h1>
            Links &amp; Downloads
          </h1>
          <p>
            This page lists EVTCC forms and files available for download, plus
            links to external resources.
          </p>
          <h2>
            Downloads
          </h2>
          <ul>
            <li>
              <a href={getDownloadUrl("EVTCC-Program-Booklet.pdf")}>
                EVTCC Program Booklet
              </a>
            </li>
            <li>
              <a href="https://register.evtcc.org">
                Online Registration
              </a>
            </li>
            <li>
              <a href={getDownloadUrl("EVTCC-Test-Site-List.pdf")}>
                Download a list of testing events
              </a>
            </li>
            <li>
              <a href={getDownloadUrl("EVTCC-Exam-Registration.pdf")}>
                Exam Registration Form
              </a>
            </li>
            <li>
              <a href={getDownloadUrl("EVTCC-Level-Application-Form.pdf")}>
                EVTCC Level Application
              </a>
            </li>
            <li>
              <a href={getDownloadUrl("EVTCC-Patch-Order-Form.pdf")}>
                EVTCC Patch Order Form
              </a>
            </li>
          </ul>
        </section>
        <section className="mt-2">
          <h2>
            Training
          </h2>
          <p>
            It is imperative to use the learning objectives that can be found
            on the <Link to="/exams">Exams &amp; Prep</Link> page whether or not you take a class. If you wish to attend
            training, we recommend reviewing the learning objectives
            beforehand so that you will be prepared to leverage training and
            know what you need to ask the instructor about.
          </p>
        </section>
        <section className="mt-2">
          <h3 className="ml-2">
            EVTCC-oriented EVT training
          </h3>
          <p className="ml-2">
            These training providers are not formally affiliated with EVTCC.
            They offer EVT training that is specifically created, at least in
            part, to prepare technicians for EVTCC tests.
          </p>
          <ul>
            <li>
              <a href="https://azfiremechanics.com/">
                AZ Fire Mechanics
              </a>{" "}
              (incl. Quarterly Mechanics’ Seminars)
            </li>
            <li>
              <a href="https://coevta.org/">
                Colorado Fire Mechanics Association
              </a>{" "}
              (annual Mechanics’ Seminars in July/August)
            </li>
            <li>
              <a href="http://www.evttraining.net/">
                Emergency Vehicle Technical Support
              </a>{" "}
              (Ocala, FL, plus <a href="https://evttraining.net/virtual-learning/">live virtual learning</a>)
            </li>
            <li>
              <a href="https://e-one.com/training/">
                E-One
              </a>{" "}
              (Monthly EVTCC Prep Classes)
            </li>
            <li>
              <a href="https://evttps.com/">
                EVT Test Preparation Services
              </a>{" "}
              (EVTCC Preparation Guides)
            </li>
            <li>
              <a href="https://firepumptraining.com/">
                Fire Pump Training LLC
              </a>{" "}
              (F3 & F7)
            </li>
            <li>
              <a href="https://www.fdsoa.org/">
                Fire Department Safety Officers Association
              </a>{" "}
              (Annual Maintenance Symposium; they bring training to you)
            </li>
            <li>
              <a href="https://www.ffca.org/evt">
                Florida Fire Chiefs’ Association
              </a>{" "}
              (Florida-wide)
            </li>
            <li>
              <a href="https://www.fvtc.edu/">
                Fox Valley Technical College
              </a>{" "}
              (Appleton, WI)
            </li>
            <li>
              <a href="https://www.greenwoodev.com/training/">
                Greenwood Emergency Vehicles
              </a>{" "}
              (MA)
            </li>
            <li>
              <a href="https://www.heatevt.com/">
                Heartland Emergency Apparatus Technicians Association
              </a>{" "}
              (August EVT Training Seminar)
            </li>
            <li>
              <a href="https://www.ifama.net/">
                Illinois Fire Apparatus Mechanics Association
              </a>
            </li>
            <li>
              <a href="http://www.newenglandfirechiefs.org/">
                New England Association of Fire Chiefs
              </a>
            </li>
            <li>
              <a href="https://oaevt.org/training-symposium/">
                Ohio Association of Emergency Vehicle Technicians
              </a>{" "}
              (Ohio Fire Academy, Reynoldsburg, OH; October EVT Training Seminar)
            </li>
            <li>
              <a href="http://www.okevt.com/">
                Oklahoma Association of Emergency Vehicle Technicians
              </a>{" "}
              (Edmond, OK)
            </li>
            <li>
              <a href="https://www.ofaema.org/">
                Oregon Fire Apparatus Equipment Mechanics Association
              </a>{" "}
              (March EVT Training Seminar)
            </li>
            <li>
              <a href="https://overhaulprep.app/evt">
                Overhaul Prep
              </a>{" "}
              (Online practice tests for EVTCC)
            </li>
            <li>
              <a href="http://www.spartaner.com/">
                Spartan Emergency Response
              </a>{" "}
              (Annual Fire Truck Training Conference)
            </li>
            <li>
              <a href="https://www.sutphen.com/training/">
                Sutphen Training Center
              </a>{" "}
              (Manufacturer EVTCC Prep Classes)
            </li>
            <li>
              <a href="https://teex.org/class/ASP150/">
                Texas A&amp;M Extension Service
              </a>{" "}
              (College Station, Texas; July Fire Pump Repair Seminar)
            </li>
            <li>
              <a href="https://taevt.org/swevt-training-conference/">
                Texas Association of EVTs
              </a>{" "}
              (Training in Fort Worth, TX; March EVT Training Seminar)
            </li>
            <li>
              <a href="https://www.washingtonfirechiefs.com/">
                Washington Fire Chiefs Association
              </a>{" "}
              (September EVT Training Seminar)
            </li>
            <li>
              <a href="https://www.waevt.com/">
                Wisconsin Association of Emergency Vehicle Technicians
              </a>{" "}
              (Appleton, WI; Quarterly EVT Training Events)
            </li>
          </ul>
        </section>
        <section className="mt-2">
          <h3 className="ml-2">
            General EVT training
          </h3>
          <p className="ml-2">
            These training providers are not formally affiliated with EVTCC.
            They offer EVT training that may be helpful for EVTCC tests, but
            is not specifically oriented to EVTCC’s tests or
            certifications.
          </p>
          <ul>
            <li>
              <a href="https://cafiremech.com/fall-spring-academy/">
                California Fire Mechanics Academy
              </a>
            </li>
            <li>
              <a href="http://www.cfema.org/">
                Connecticut Fire Equipment Mechanics Association
              </a>
            </li>
          </ul>
        </section>
        <section className="mt-2">
          <h2>
            Others in the Industry
          </h2>
          <p>
            As with training providers listed on this page, none of the
            organizations listed here are formally affiliated with EVTCC. We
            provide these links because they may be of interest to you.
          </p>
        </section>
        <section className="ml-2 mt-2">
          <h3>
            Manufacturers
          </h3>
          <ul>
            <li>
              <a href="https://www.darley.com/">
                Darley
              </a>
            </li>
            <li>
              <a href="https://e-one.com/">
                E-One
              </a>{" "}
              (Ocala, FL)
            </li>
            <li>
              <a href="https://www.haleproducts.com/">
                Hale Products
              </a>
            </li>
            <li>
              <a href="https://www.piercemfg.com/fire-trucks/products-overview/">
                Pierce
              </a>
            </li>
            <li>
              <a href="https://rosenbaueramerica.com/">
                Rosenbauer
              </a>
            </li>
            <li>
              <a href="https://www.waterousco.com/">
                Waterous Co.
              </a>
            </li>
          </ul>
        </section>
        <section className="ml-2 mt-2">
          <h3>
            Industry Advocates, Associations, Etc.
          </h3>
          <ul>
            <li>
              <a href="https://www.fdic.com/">
                FDIC International
              </a>
            </li>
            <li>
              <a href="https://www.firerescue1.com/fire-chief">
                Fire Chief Magazine
              </a>
            </li>
            <li>
              <a href="https://www.firefusionconference.com/">
                Fire Fusion
              </a>{" "}
              (formerly Firehouse Expo; Charleston, SC)
            </li>
            <li>
              <a href="https://www.iafc.org/">
                International Association of Fire Chiefs
              </a>
            </li>
            <li>
              <a href="https://www.lievt.org/">
                Long Island Association of EVTs
              </a>
            </li>
            <li>
              <a href="https://www.ase.com/">
                National Institute for Automotive Service Excellence
              </a>
            </li>
            <li>
              <a href="https://www.nfpa.org/">
                National Fire Protection Association
              </a>{" "}
              (NFPA)
            </li>
            <li>
              <a href="https://www.sgfma.org/">
                Southeast Governmental Fleet Managers Association
              </a>
            </li>
          </ul>
        </section>
      </PageContent>
    </>
  );
}
